/* This file is maintained by LLMs.

This is a private client database adapter that provides common functionality for all client adapters.
It extracts the common patterns found across conversation, task, transcript, message, and workspace adapters.

This adapter sits between ClientDatabaseAdapter and the specific table adapters, providing:
- Common constructor pattern with userId, authToken, and adapterConfig
- Default implementations for common methods
- Standardized REST layer implementations
- Common error handling patterns

*/

import { ClientDatabaseAdapter, parseDate } from './base';

import type { AdapterConfig, UserAuth } from '@repo/common';
import type { BaseModel } from '@repo/common/types/Sqlite';
import {
  getAdapterItemById,
  getAdapterItems,
  getAdapterItemsByIds,
  sendAdapterItem,
  sendAdapterItems
} from '@repo/sharedlib/src/data/adapter.data';
import { userAuthStore } from '@repo/sharedlib/src/stores/auth.store';
import { processBatchesWithConcurrency } from '@repo/toolslib';
import { get } from 'svelte/store';
import type { CommonItem, DeduplicationStrategy, MinimalItem } from './types';

// ─── PRIVATE CLIENT DATABASE ADAPTER ───

export abstract class PrivateClientDatabaseAdapter<
  SqliteRowType extends Record<string, any> & { id: string; updatedAt: string; createdAt: string },
  CommonItemType extends CommonItem
> extends ClientDatabaseAdapter<SqliteRowType, CommonItemType> {
  protected userId: string;
  protected authToken: Pick<UserAuth, 'idToken' | 'refreshToken'>;
  protected adapterConfig?: AdapterConfig;

  // Simple in-memory request coalescer for get-by-id calls to use batch endpoint
  private _batchTimer?: ReturnType<typeof setTimeout>;
  private _batchWindowMs = 50;
  private _pendingRequests: Array<{
    id: string;
    minimal: boolean;
    resolve: (item: CommonItemType | null) => void;
    reject: (err: unknown) => void;
  }> = [];

  constructor(
    sqliteModel: BaseModel<any, SqliteRowType>,
    tableName: string,
    userId: string,
    authToken: Pick<UserAuth, 'idToken' | 'refreshToken'>,
    adapterConfig?: AdapterConfig
  ) {
    super(sqliteModel, tableName);
    this.userId = userId;
    this.authToken = authToken;
    this.adapterConfig = adapterConfig;

    // Debug logging for missing auth tokens
    if (!authToken || !authToken.idToken || !authToken.refreshToken) {
      console.error(`[${tableName}] Client adapter created with missing auth tokens:`, {
        hasAuthToken: !!authToken,
        hasIdToken: !!authToken?.idToken,
        hasRefreshToken: !!authToken?.refreshToken,
        userId,
        tableName
      });
    }
  }

  // ─── DEFAULT IMPLEMENTATIONS FOR COMMON PATTERNS ───

  /**
   * Default implementation for upsertListToSqliteImpl
   * Uses the standard bulkUpsertItemsRaw method from the BaseModel interface
   */
  protected async upsertListToSqliteImpl(rows: SqliteRowType[]): Promise<void> {
    // Use the standard bulkUpsertItemsRaw method from BaseModel
    await this.sqliteModel.bulkUpsertItemsRaw(rows);
  }

  /**
   * Default implementation for minimalSqliteRowToItem
   * Assumes the row has id, updatedAt, and name fields
   */
  protected minimalSqliteRowToItem(
    row: Pick<SqliteRowType, 'id' | 'updatedAt'> & Record<string, any>
  ): MinimalItem {
    const parsed = parseDate(row.updatedAt);
    const safeDate =
      parsed && !isNaN(parsed.getTime()) ? parsed : new Date('1970-01-01T00:00:00.000Z');
    return {
      id: row.id,
      updatedAt: safeDate,
      createdAt: safeDate,
      type: row.type || this.tableName,
      name: row.name || row.id
    };
  }

  /**
   * Default implementation for getMinimalFromSqlite
   * Assumes the model has findAllObj method and returns objects with id, updatedAt, type, and name
   * Also includes fields needed for deduplication strategy (like userId for workspace adapters)
   */
  protected async getMinimalFromSqlite(): Promise<
    Array<Pick<SqliteRowType, 'id' | 'updatedAt'> & Record<string, unknown>>
  > {
    // Read raw rows to avoid double mapping and align with driver reality
    const items = await (this.sqliteModel as any).findAllRows({ paranoid: true });
    const mapped = items.map((item: any) => {
      // Normalize updatedAt to a safe ISO string
      let updatedAt: string = '1970-01-01T00:00:00.000Z';
      try {
        if (item.updatedAt && typeof item.updatedAt === 'string') {
          const parsedDate = parseDate(item.updatedAt);
          if (parsedDate && !isNaN(parsedDate.getTime())) {
            updatedAt = parsedDate.toISOString();
          }
        }
      } catch {
        // keep fallback
      }

      const minimalItem: Record<string, unknown> = {
        id: item.id,
        updatedAt,
        type: item.type || this.tableName,
        name: item.name || item.id
      };

      // Add userId if it's needed for deduplication strategy
      const strategy = this.getDeduplicationStrategy();
      if (strategy.type === 'custom') {
        (minimalItem as any).userId = this.userId;
      }

      return minimalItem;
    }) as unknown as Array<Pick<SqliteRowType, 'id' | 'updatedAt'> & Record<string, unknown>>;

    // Filter out any invalid rows lacking an id to avoid poisoning checksum-based sync
    const valid = mapped.filter((row: any) => typeof row?.id === 'string' && row.id.length > 0);
    if (valid.length !== mapped.length) {
      console.warn(
        `[${this.tableName}] getMinimalFromSqlite(): filtered out ${mapped.length - valid.length} invalid rows with missing ids`
      );
    }
    return valid;
  }

  /**
   * Default implementation for getCommonItemById with minimal support
   * Wraps getAdapterItemById and includes the minimal parameter
   */
  protected async getCommonItemById(
    id: string,
    minimal: boolean = false
  ): Promise<CommonItemType | null> {
    // Coalesce per-ID requests into a batch POST to reduce HTTP chatter
    return await new Promise<CommonItemType | null>((outerResolve, outerReject) => {
      this._pendingRequests.push({ id, minimal, resolve: outerResolve, reject: outerReject });

      if (this._batchTimer) return;
      this._batchTimer = setTimeout(async () => {
        const requests = this._pendingRequests.splice(0, this._pendingRequests.length);
        this._batchTimer = undefined;

        // Group by minimal flag
        const groups = new Map<boolean, typeof requests>();
        for (const req of requests) {
          if (!groups.has(req.minimal)) groups.set(req.minimal, [] as any);
          groups.get(req.minimal)!.push(req);
        }

        for (const [groupMinimal, group] of groups) {
          const ids = Array.from(new Set(group.map((r) => r.id)));

          try {
            const authTokenWithFallback = this.getAuthTokenWithFallback();
            const items = await getAdapterItemsByIds<CommonItemType>(
              this.tableName,
              ids,
              authTokenWithFallback,
              this.adapterConfig,
              groupMinimal
            );
            // Validate response shape to avoid TypeErrors when tests don't stub this API
            if (!Array.isArray(items)) {
              throw new Error('Batch fetch returned invalid response');
            }
            const byId = new Map(items.map((it) => [it.id, it]));
            for (const r of group) {
              r.resolve(byId.get(r.id) ?? null);
            }
          } catch (batchErr) {
            // Fallback: resolve each request with individual GET
            await Promise.all(
              group.map(async (r) => {
                try {
                  const authTokenWithFallback = this.getAuthTokenWithFallback();
                  const item = await getAdapterItemById<CommonItemType>(
                    this.tableName,
                    r.id,
                    authTokenWithFallback,
                    this.adapterConfig,
                    r.minimal
                  );
                  r.resolve(item);
                } catch {
                  // Normalize error message for tests and consistent UX
                  r.reject(new Error(`Failed to get common item ${r.id} from server`));
                }
              })
            );
          }
        }
      }, this._batchWindowMs);
    });
  }

  /**
   * Batched retrieval of common items by IDs using REST batch endpoint
   */
  protected async getCommonItemsByIds(
    ids: string[],
    minimal: boolean = false
  ): Promise<CommonItemType[]> {
    if (ids.length === 0) return [];
    try {
      const authTokenWithFallback = this.getAuthTokenWithFallback();
      const items = await getAdapterItemsByIds<CommonItemType>(
        this.tableName,
        ids,
        authTokenWithFallback,
        this.adapterConfig,
        minimal
      );
      return items;
    } catch (error) {
      console.error(`Error getting common items by IDs from server:`, error);
      throw new Error('Failed to get common items by IDs from server');
    }
  }

  /**
   * Default implementation for getCommonItems
   * Wraps getAdapterItems with proper error handling
   */
  protected async getCommonItems(minimal: boolean = false): Promise<CommonItemType[]> {
    try {
      // Debug logging for missing auth tokens during API calls
      if (!this.authToken || !this.authToken.idToken || !this.authToken.refreshToken) {
        console.error(`[${this.tableName}] Missing auth tokens during getCommonItems call:`, {
          hasAuthToken: !!this.authToken,
          hasIdToken: !!this.authToken?.idToken,
          hasRefreshToken: !!this.authToken?.refreshToken,
          userId: this.userId,
          tableName: this.tableName
        });
      }

      const authTokenWithFallback = this.getAuthTokenWithFallback();

      const items = await getAdapterItems<CommonItemType>(
        this.tableName,
        authTokenWithFallback,
        this.adapterConfig,
        minimal
      );
      return items;
    } catch (error) {
      console.error('Error getting common items from server:', error);
      throw new Error('Failed to get common items from server');
    }
  }

  /**
   * Optimized toSqlite sync
   * - If SQLite is empty: fallback to full fetch
   * - Otherwise: use server checksums to fetch only changed IDs
   */
  protected async syncToSqlite(
    onProgress?: (operation: string, itemsComplete: number, totalItems: number) => void
  ): Promise<void> {
    // Get minimal items from SQLite and sanitize
    const sqliteMinimalRaw = await this.getMinimalFromSqlite();
    const sqliteMinimal = sqliteMinimalRaw.filter(
      (row: any) => typeof row?.id === 'string' && row.id
    );

    // Build local checksum map (updatedAt|deletedAt)
    const localChecksums = new Map<string, string>();
    const ids: string[] = [];
    for (const row of sqliteMinimal) {
      const id = row.id;
      const updatedAt = row.updatedAt;
      const deletedAt = row.deletedAt ?? '';
      const checksum = `${updatedAt}|${String(deletedAt)}`;
      localChecksums.set(id, checksum);
      ids.push(id);
    }

    // Get minimal server items first to identify what needs to be synced
    const serverMinimalItems = await this.getCommonItems(true);

    // Determine which items are new or changed (using minimal data)
    const itemsToFetchIds: string[] = [];
    for (const serverItem of serverMinimalItems) {
      const local = localChecksums.get(serverItem.id);
      if (!local) {
        // New item that doesn't exist in SQLite
        itemsToFetchIds.push(serverItem.id);
      } else {
        // Check if item has changed
        const serverChecksum = `${serverItem.updatedAt.toISOString()}|${String(serverItem.deletedAt || '')}`;
        if (local !== serverChecksum) {
          itemsToFetchIds.push(serverItem.id);
        }
      }
    }

    if (itemsToFetchIds.length === 0) {
      if (onProgress) onProgress(`Syncing ${this.tableName} items to local database...`, 0, 0);
      return;
    }

    // Now fetch full content only for items that need to be synced
    const itemsToFetch: CommonItemType[] = [];
    let fetchProgress = 0;
    const totalToFetch = itemsToFetchIds.length;

    if (onProgress) onProgress(`Fetching ${this.tableName} items from server...`, 0, totalToFetch);

    // Fetch items in batches to avoid overwhelming the server
    const batchSize = this.adapterConfig?.batchSize || 100;
    for (let i = 0; i < itemsToFetchIds.length; i += batchSize) {
      const batch = itemsToFetchIds.slice(i, i + batchSize);
      const batchItems = await this.getCommonItemsByIds(batch, false); // Get full content
      itemsToFetch.push(...batchItems);

      fetchProgress += batch.length;
      if (onProgress)
        onProgress(`Fetching ${this.tableName} items from server...`, fetchProgress, totalToFetch);
    }

    // Convert items to SQLite rows and upsert
    const sqliteRows = itemsToFetch.map((item: CommonItemType) =>
      this.itemToSqliteRowWithNullConversion(item)
    );

    let completed = 0;
    const total = sqliteRows.length;
    if (onProgress) onProgress(`Syncing ${this.tableName} items to local database...`, 0, total);

    // Use existing upsert helper with progress callback
    await this.upsertListToSqliteWithProgress(sqliteRows, (_operation, itemsComplete, _total) => {
      completed = itemsComplete;
      if (onProgress)
        onProgress(`Syncing ${this.tableName} items to local database...`, completed, total);
    });
  }

  /**
   * Default implementation for sendCommonItem
   * Wraps sendAdapterItem with proper error handling
   */
  protected async sendCommonItem(item: CommonItemType): Promise<void> {
    // Validate that the item is complete before sending to cloud
    this.validateItemForCloud(item);

    // Debug logging for missing auth tokens during API calls
    if (!this.authToken || !this.authToken.idToken || !this.authToken.refreshToken) {
      console.error(`[${this.tableName}] Missing auth tokens during sendCommonItem call:`, {
        hasAuthToken: !!this.authToken,
        hasIdToken: !!this.authToken?.idToken,
        hasRefreshToken: !!this.authToken?.refreshToken,
        userId: this.userId,
        tableName: this.tableName,
        itemId: item.id
      });
    }

    const authTokenWithFallback = this.getAuthTokenWithFallback();

    // Normalize fields that must explicitly clear sentinels on the server
    const normalizedItem: CommonItemType = {
      ...(item as any),
      // Ensure deletedAt is explicitly null when undefined so server clears epoch sentinels
      deletedAt: (item as any).deletedAt ?? null
    };

    await sendAdapterItem<CommonItemType>(
      this.tableName,
      normalizedItem,
      authTokenWithFallback,
      this.adapterConfig
    );
  }

  /**
   * Handle constraint errors from batch response
   */
  private handleConstraintErrors(response: any): void {
    if (!response.errors || response.errors.length === 0) return;

    const foreignKeyErrors = response.errors.filter(
      (e: any) => e.error.code === 'FOREIGN_KEY_CONSTRAINT'
    );
    const uniqueErrors = response.errors.filter((e: any) => e.error.code === 'UNIQUE_CONSTRAINT');

    if (foreignKeyErrors.length > 0) {
      console.warn(
        `[${this.tableName}] Foreign key constraint errors in batch: ` +
        `${foreignKeyErrors.length} items failed, ${response.syncedCount} succeeded. ` +
        `Failed items will be retried in future syncs when dependencies are available.`
      );

      // Log individual foreign key errors for debugging
      for (const fkError of foreignKeyErrors) {
        console.debug(
          `[${this.tableName}] Foreign key error for item ${fkError.itemId}:`,
          fkError.error
        );
      }
    }

    if (uniqueErrors.length > 0) {
      console.warn(
        `[${this.tableName}] Unique constraint errors in batch: ` +
        `${uniqueErrors.length} items failed, ${response.syncedCount} succeeded. ` +
        `These items already exist in the database and will be skipped.`
      );

      // Log individual unique constraint errors for debugging
      for (const uniqueError of uniqueErrors) {
        console.debug(
          `[${this.tableName}] Unique constraint error for item ${uniqueError.itemId}:`,
          uniqueError.error
        );
      }
    }
  }

  /**
   * Process a batch of items with error handling
   */
  private async processBatch(
    batch: CommonItemType[],
    authTokenWithFallback: Pick<UserAuth, 'idToken' | 'refreshToken'>
  ): Promise<void> {
    // Validate that all items are complete before sending to cloud
    for (const item of batch) {
      this.validateItemForCloud(item);
    }

    // Normalize batch to ensure deletedAt is explicitly null when undefined
    const normalizedBatch = batch.map((i) => ({
      ...(i as any),
      deletedAt: (i as any).deletedAt ?? null
    }));

    const response = await sendAdapterItems(
      this.tableName,
      normalizedBatch,
      authTokenWithFallback,
      this.adapterConfig
    );

    // Handle constraint errors gracefully
    this.handleConstraintErrors(response);

    // Throw error for non-constraint failures
    // Only throw if there are no constraint errors (constraint errors are handled gracefully)
    if (!response.success && (!response.errors || response.errors.length === 0)) {
      throw new Error(`Failed to sync ${this.tableName} batch: ${response.error}`);
    }
  }

  /**
   * Log missing auth tokens for debugging
   */
  private logMissingAuthTokens(methodName: string, itemCount?: number): void {
    if (!this.authToken || !this.authToken.idToken || !this.authToken.refreshToken) {
      console.error(`[${this.tableName}] Missing auth tokens during ${methodName} call:`, {
        hasAuthToken: !!this.authToken,
        hasIdToken: !!this.authToken?.idToken,
        hasRefreshToken: !!this.authToken?.refreshToken,
        userId: this.userId,
        tableName: this.tableName,
        ...(itemCount !== undefined && { itemCount })
      });
    }
  }

  /**
   * Default implementation for sendCommonItems with connection pool
   * Uses controlled concurrency to prevent overwhelming the server
   */
  protected async sendCommonItems(items: CommonItemType[]): Promise<void> {
    if (items.length === 0) return;

    this.logMissingAuthTokens('sendCommonItems', items.length);
    const authTokenWithFallback = this.getAuthTokenWithFallback();

    // Use connection pool for controlled concurrency with batch processing
    await processBatchesWithConcurrency(
      items,
      (batch) => this.processBatch(batch, authTokenWithFallback),
      {
        maxConcurrent: this.adapterConfig?.maxConcurrent,
        batchSize: this.adapterConfig?.batchSize,
        delayBetweenBatches: this.adapterConfig?.delayBetweenBatches
      }
    );
  }

  /**
   * Default implementation for sendCommonItemsWithProgress with connection pool
   * Uses controlled concurrency to prevent overwhelming the server with progress tracking
   */
  protected async sendCommonItemsWithProgress(
    items: CommonItemType[],
    onProgress?: (operation: string, itemsComplete: number, totalItems: number) => void
  ): Promise<void> {
    if (items.length === 0) return;

    this.logMissingAuthTokens('sendCommonItemsWithProgress', items.length);
    const authTokenWithFallback = this.getAuthTokenWithFallback();

    // Use connection pool for controlled concurrency with progress tracking
    await processBatchesWithConcurrency(
      items,
      (batch) => this.processBatch(batch, authTokenWithFallback),
      {
        maxConcurrent: this.adapterConfig?.maxConcurrent,
        batchSize: this.adapterConfig?.batchSize,
        delayBetweenBatches: this.adapterConfig?.delayBetweenBatches
      },
      (completed, total) => {
        if (onProgress) {
          onProgress(`Syncing ${this.tableName} to cloud...`, completed, total);
        }
      }
    );
  }

  /**
   * Default implementation for getNewerItem
   * Compares updatedAt timestamps. When equal, return 'equal' so normal sync skips
   * transferring data; any repair will be handled by the verification step.
   */
  protected getNewerItem(
    sqliteItem: CommonItemType,
    commonItem: CommonItemType
  ): 'sqlite' | 'common' | 'equal' {
    const sqliteTime = sqliteItem.updatedAt.getTime();
    const commonTime = commonItem.updatedAt.getTime();

    // Debug logging for timestamp comparison
    // if (Math.abs(sqliteTime - commonTime) < 1000) {
    //   // Within 1 second
    //   console.log(
    //     `[${this.tableName}] Timestamp comparison for ${sqliteItem.id}:`,
    //     `SQLite: ${sqliteItem.updatedAt.toISOString()} (${sqliteTime})`,
    //     `Common: ${commonItem.updatedAt.toISOString()} (${commonTime})`,
    //     `Diff: ${sqliteTime - commonTime}ms`,
    //     `sqliteTime > commonTime: ${sqliteTime > commonTime}`,
    //     `sqliteTime < commonTime: ${sqliteTime < commonTime}`
    //   );
    // }

    if (sqliteTime > commonTime) return 'sqlite';
    if (sqliteTime < commonTime) return 'common';
    return 'equal';
  }

  /**
   * Default implementation for getDeduplicationStrategy
   * Uses 'none' strategy with item.id as the key
   * Subclasses can override this for custom deduplication
   */
  protected getDeduplicationStrategy(): DeduplicationStrategy<CommonItemType> {
    return {
      type: 'none',
      getKey: (item: CommonItemType) => item.id
    };
  }

  // ─── UTILITY METHODS ───

  /**
   * Get the userId for this adapter
   */
  protected getUserId(): string {
    return this.userId;
  }

  /**
   * Get the auth token for this adapter
   */
  protected getAuthToken(): Pick<UserAuth, 'idToken' | 'refreshToken'> {
    return this.authToken;
  }

  /**
   * Get the adapter config for this adapter
   */
  protected getAdapterConfig(): AdapterConfig | undefined {
    return this.adapterConfig;
  }

  /**
   * Get auth tokens with fallback to auth store if missing
   */
  protected getAuthTokenWithFallback(): Pick<UserAuth, 'idToken' | 'refreshToken'> {
    // If we have valid auth tokens, use them
    if (this.authToken?.idToken && this.authToken?.refreshToken) {
      return this.authToken;
    }

    // Try to get from auth store as fallback
    try {
      const $userAuth = get(userAuthStore);
      if ($userAuth?.idToken && $userAuth?.refreshToken) {
        console.warn(
          `[${this.tableName}] Using auth tokens from store as fallback for user ${this.userId}`
        );
        return {
          idToken: $userAuth.idToken,
          refreshToken: $userAuth.refreshToken
        };
      }
    } catch (error) {
      console.error(`[${this.tableName}] Error getting auth tokens from store:`, error);
    }

    // Return original tokens (will cause error in API calls)
    return this.authToken;
  }
}
